import { Grid, useMediaQuery } from '@material-ui/core';
import { StyledShortBody } from 'components/ItemInfoCard/styles';
import { StyledCaptionsBase } from 'components/MenuItem/styles';
import { useFormikContext } from 'formik';
import { AssuranceIcon } from 'images';
import { get } from 'lodash';
import { DropzoneAreaBase, FileObject } from 'material-ui-dropzone';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { asyncForEach, blobToBase64, compress } from 'utils/amcUtils';
import formatBytes from 'utils/formatBytes';
import { COMMON_ACCEPTED_FILE_TYPES, MEGA_BYTE } from 'utils/globalConst';
import labels from 'utils/labels';
import { updatePACObject } from '../../../store/pac/actions';
import {
  ContentSecondTitle,
  ErrorTitle,
  FolderTypeSecondaryContent,
  ImageWrapper,
  PrimaryTitleCard,
  StyledDropZoneContainer,
} from '../DeclareFolderProcess/styles';

type UploadFilesProps = {
  title: string;
  secondTitle?: string;
  items: { icon: string; title: string }[];
  filesLimit?: number;
};

const UploadFiles = ({
  title,
  secondTitle,
  items,
  filesLimit,
}: UploadFilesProps) => {
  const { values } = useFormikContext<any>();
  const [fileUploded, setFileUploded] = useState(values.listFiles);
  const dispatch = useDispatch();
  const pacObject = useSelector((state: RootStateOrAny) =>
    state.getIn(['pac', 'pacObject']),
  );

  const intl = useIntl();

  const { setFieldValue, errors, touched } = useFormikContext<any>();

  const isNotMobile = useMediaQuery('(min-width:960px)');

  return (
    <Grid
      spacing={3}
      container
      style={{
        maxWidth: 600,
        maxHeight: isNotMobile ? 370 : 400,
        overflow: 'auto',
      }}
    >
      <Grid item>
        <PrimaryTitleCard style={{ marginBottom: 8 }} fontSize="18px">
          {title}
        </PrimaryTitleCard>
        {secondTitle && (
          <ContentSecondTitle style={{ marginBottom: 8 }} fontSize="14px">
            {secondTitle}
          </ContentSecondTitle>
        )}
      </Grid>
      <Grid item xs={12} sm={12} lg={12} style={{ marginBottom: 8 }}>
        <FolderTypeSecondaryContent>
          {items.map((item) => {
            return (
              <Grid container style={{ alignItems: 'center' }} spacing={2}>
                <Grid item xs={2} sm={1}>
                  <img
                    src={AssuranceIcon[item.icon]}
                    alt="optique icon - saham assurance"
                  />
                </Grid>
                <Grid style={{ alignSelf: 'center' }} item xs={10} sm={11}>
                  <PrimaryTitleCard>{item.title}</PrimaryTitleCard>
                </Grid>
              </Grid>
            );
          })}
        </FolderTypeSecondaryContent>
      </Grid>

      <Grid item xs={12} sm={12} lg={12}>
        <StyledDropZoneContainer container justify="flex-start">
          <DropzoneAreaBase
            fileObjects={fileUploded}
            acceptedFiles={COMMON_ACCEPTED_FILE_TYPES}
            maxFileSize={20 * MEGA_BYTE}
            getDropRejectMessage={
              /* istanbul ignore next */
              (file: any, _, maxFileSize) => {
                if (file.size > maxFileSize) {
                  return intl.formatMessage(
                    {
                      id: labels.fileSizeTooLarge,
                    },
                    { maxFileSize: '20 Mo' },
                  );
                }
                return '';
              }
            }
            onAdd={
              /* istanbul ignore next */ async (newFileObjs) => {
                const newUploadedDocuments: any[] = [];
                let i: number = pacObject.TmpListFiles.length;
                let totalSize = 0;

                await asyncForEach(newFileObjs, async (p: FileObject) => {
                  const compressedImage: any = p;
                  if (compressedImage.file.type.includes('image')) {
                    compressedImage.file = await compress(compressedImage.file);
                    compressedImage.data = await blobToBase64(
                      compressedImage.file,
                    );
                  }

                  newUploadedDocuments.push({
                    ...compressedImage,
                    index: ++i,
                  });
                });

                pacObject.TmpListFiles.concat(newUploadedDocuments).map(
                  (f: any) => (totalSize += f.file.size / 1000000),
                );

                if (totalSize <= 20) {
                  dispatch(
                    updatePACObject({
                      TmpListFiles: [
                        ...pacObject.TmpListFiles.concat(newUploadedDocuments),
                      ],
                    }),
                  );
                  setFileUploded(
                    fileUploded.concat(newUploadedDocuments as never),
                  );
                  setFieldValue(
                    'listFiles',
                    fileUploded.concat(newUploadedDocuments as never),
                  );
                  return;
                }

                alert(
                  intl.formatMessage(
                    {
                      id: labels.fileSizeTooLarge,
                    },
                    { maxFileSize: '20 Mo' },
                  ),
                );
              }
            }
            onDelete={
              /* istanbul ignore next */ (deletedFileObj) => {
                const documents = fileUploded?.filter(
                  (document: any) =>
                    document.file.name !== deletedFileObj.file.name,
                );
                dispatch(
                  updatePACObject({
                    TmpListFiles: [...documents],
                  }),
                );
                setFileUploded(documents);
                setFieldValue('listFiles', documents);
              }
            }
            getPreviewIcon={
              /* istanbul ignore next */ (file) => {
                const uploadedFile: any = file;
                return (
                  uploadedFile && (
                    <ImageWrapper container>
                      <Grid item xs={2}>
                        <img src={get(AssuranceIcon, 'jpeg')} alt="jpeg" />
                      </Grid>
                      <Grid style={{ marginLeft: 5 }} item xs={8} sm={4}>
                        <StyledShortBody color="#616C78" weight="500">
                          {'Document ' + uploadedFile.index}
                        </StyledShortBody>
                        <StyledCaptionsBase>
                          {formatBytes(file.file.size)}
                        </StyledCaptionsBase>
                      </Grid>
                    </ImageWrapper>
                  )
                );
              }
            }
            dropzoneText={intl.formatMessage({
              id: 'signup_verify_identity.drop_zone_text',
            })}
            showPreviewsInDropzone={false}
            showPreviews={true}
            previewText=""
            previewGridProps={{
              container: {
                spacing: 2,
                direction: 'row',
                dir: 'ltr',
              },
              item: {
                xs: 12,
              },
            }}
            getFileAddedMessage={
              /* istanbul ignore next */ (fileName: string) =>
                `Document ${pacObject.TmpListFiles.length + 1} ` +
                intl.formatMessage({
                  id: 'amc.file.added',
                })
            }
            getFileRemovedMessage={
              /* istanbul ignore next */ (fileName: string) =>
                `${fileName} ` +
                intl.formatMessage({
                  id: 'amc.file.deleted',
                })
            }
            getFileLimitExceedMessage={
              /* istanbul ignore next */ () =>
                intl.formatMessage({
                  id: 'amc.droparea.file.limit.exceed.message.1',
                }) +
                ` ${filesLimit}` +
                intl.formatMessage({
                  id: 'amc.droparea.file.limit.exceed.message.2',
                })
            }
            filesLimit={filesLimit ?? 10}
          />
        </StyledDropZoneContainer>
      </Grid>
      <ErrorTitle>
        {touched['listFiles'] && errors['listFiles'] && errors['listFiles']}
      </ErrorTitle>
    </Grid>
  );
};
export default UploadFiles;
